// Tiny dependency-free SPA router. The app reflects its current view + selected
// project in the URL path so a hard reload restores the page (instead of
// always bouncing back to the chat dashboard).
//
//   /                      -> chat (no project / default)
//   /chat/<projectId>      -> chat with a specific project selected
//   /builds/<projectId>    -> builds page for a project
//   /qa                    -> QA tests overview (every project)
//   /qa/<projectId>        -> QA tests page for a project (its test runs)
//   /qa/<projectId>/tests  -> the same page's Tests tab: every test in the repo
//   /qa/<projectId>/runs/<runId> -> one test run (also ?run=<runId>, the old PR-comment link;
//                             ?share=<token> opens it without login)
//   /review/<projectId>    -> Code Review queue page for a project
//   /admin                 -> admin users page
//   /invite                -> redeem a one-time invite link (unauthenticated)
//
// Unknown paths fall back to chat so a stale or hand-edited URL never strands
// the user on a blank screen.
//
// `/invite` carries its token in the URL fragment, never a path segment, so it
// takes no project id and never round-trips one. A fragment is not sent to any
// server, which keeps the token out of access logs and Referer headers.

export type AppView = "chat" | "admin" | "builds" | "qa" | "qaHome" | "review" | "invite";

/** A project's QA page: its test runs, or every test its repo has. */
export type QaTab = "runs" | "tests";

export interface ParsedRoute {
  view: AppView;
  projectId: string | null;
  /** The test run open on a project's QA page; only set for the "qa" view. */
  runId?: string;
  /** Set (to "tests") only on /qa/<project>/tests. */
  tab?: QaTab;
  /** The run's share token (?share=, on the link CI posts): opens it without login. */
  share?: string;
}

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/** Parse the SPA route from a URL pathname. */
export function parseRoute(pathname: string, search = ""): ParsedRoute {
  const parts = pathname.split("/").map((s) => s.trim()).filter(Boolean);
  const section = parts[0] ?? "";
  const projectId = parts[1] ? safeDecode(parts[1]) : null;

  if (section === "builds") return { view: "builds", projectId };
  if (section === "qa") {
    if (!projectId) return { view: "qaHome", projectId: null };
    if (parts[2] === "tests") return { view: "qa", projectId, tab: "tests" };
    const runId = parts[2] === "runs" && parts[3] ? safeDecode(parts[3]) : new URLSearchParams(search).get("run");
    const share = new URLSearchParams(search).get("share");
    if (!runId) return { view: "qa", projectId };
    return share ? { view: "qa", projectId, runId, share } : { view: "qa", projectId, runId };
  }
  if (section === "review") return { view: "review", projectId };
  if (section === "admin") return { view: "admin", projectId: null };
  if (section === "invite") return { view: "invite", projectId: null };
  if (section === "chat") return { view: "chat", projectId };
  return { view: "chat", projectId: null };
}

/** Build the canonical pathname for a view + selected project. */
export function routeToPath(view: AppView, projectId: string | null, runId?: string | null, tab: QaTab = "runs"): string {
  if (view === "builds" && projectId) return `/builds/${encodeURIComponent(projectId)}`;
  if (view === "qa" && projectId && runId) return `/qa/${encodeURIComponent(projectId)}/runs/${encodeURIComponent(runId)}`;
  if (view === "qa" && projectId && tab === "tests") return `/qa/${encodeURIComponent(projectId)}/tests`;
  if (view === "qa" && projectId) return `/qa/${encodeURIComponent(projectId)}`;
  if (view === "qaHome" || view === "qa") return "/qa";
  if (view === "review" && projectId) return `/review/${encodeURIComponent(projectId)}`;
  if (view === "admin") return "/admin";
  if (view === "invite") return "/invite";
  if (view === "chat" && projectId) return `/chat/${encodeURIComponent(projectId)}`;
  return "/";
}
